import React, { FC } from 'react'
import { CrewComplaint } from '../core/_requests'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

type Props = {
  visible: boolean
  complaint: CrewComplaint | null
  onClose: () => void
}

const badge = (status: string) => {
  if (status === 'DRAFT') return 'badge-light-secondary'
  if (status === 'SUBMITTED') return 'badge-light-warning'
  if (status === 'REVIEWED') return 'badge-light-success'
  if (status === 'REJECTED') return 'badge-light-danger'
  return 'badge-light'
}

const complaintTypeLabel = (value?: string | null) => {
  if (!value) return '-'
  const key = value.toLowerCase().replace(/[_\s-]+/g, '')
  if (key === 'bullying') return 'Bullying'
  if (key === 'physicalharassment') return 'Physical Harassment'
  if (key === 'mentalharassment') return 'Mental Harassment'
  if (key === 'sexualharassment') return 'Sexual Harassment'
  const map: Record<string, string> = {
    BULLYING: 'Bullying',
    PHYSICAL_HARASSMENT: 'Physical Harassment',
    MENTAL_HARASSMENT: 'Mental Harassment',
    SEXUAL_HARASSMENT: 'Sexual Harassment',
  }
  return map[value] || value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const hasAnyValue = (...vals: Array<string | null | undefined>) => vals.some((v) => !!v && String(v).trim().length > 0)

const workflowStageLabel = (stage?: string | null) => {
  if (!stage) return ''
  if (stage === 'HOD_REVIEW') return 'HOD Review'
  if (stage === 'MASTER_REVIEW') return 'Review'
  if (stage === 'OFFICE_REVIEW') return 'Office Review'
  if (stage === 'AUTHORITIES_REVIEW') return 'Authorities Review'
  return stage
}

const ViewCrewComplaintModal: FC<Props> = ({ visible, complaint, onClose }) => {
  if (!visible || !complaint) return null

  const complaintAny = complaint as any

  const hasOnboardData = hasAnyValue(
    complaint.hodMasterResponse,
    complaint.hodMasterResponseDate,
    complaint.onboardPreventRecurrence,
    complaint.hodSignature,
    complaint.masterSignature
  )

  const hasOfficeData = hasAnyValue(
    complaint.officeActionTaken,
    complaint.officeActionDate,
    complaint.officePreventRecurrence,
    complaint.officeWelfareOfficerSignature,
    complaint.officeResolutionDate,
    complaint.officeRemarks
  )

  const hasAuthoritiesData = hasAnyValue(
    complaint.authoritiesActionTaken,
    complaint.authoritiesActionDate,
    complaint.authoritiesPreventRecurrence,
    complaint.authoritiesWelfareOfficerSignature,
    complaint.authoritiesResolutionDate
  )

  const hasOnboardReviewMeta =
    complaint.onboardResolved === true ||
    complaint.onboardResolved === false ||
    hasAnyValue(complaint.onboardReviewRole || '', complaint.onboardReviewedBy, complaint.onboardReviewedAt)

  const hasOfficeReviewMeta =
    complaint.officeResolved === true ||
    complaint.officeResolved === false ||
    hasAnyValue(complaint.officeActionDate, complaint.officeResolutionDate)

  const hasAuthoritiesReviewMeta = hasAnyValue(complaint.authoritiesActionDate, complaint.authoritiesResolutionDate)

  const onboardReviewRole = String(complaint.onboardReviewRole || '').toUpperCase()
  const hodResponse =
    complaintAny.hodResponse ||
    (onboardReviewRole !== 'MASTER' ? complaint.hodMasterResponse || '' : '')
  const hodResponseDate =
    complaintAny.hodResponseDate ||
    (onboardReviewRole !== 'MASTER' ? complaint.hodMasterResponseDate || '' : '')
  const masterResponse =
    complaintAny.masterResponse ||
    (onboardReviewRole === 'MASTER' ? complaint.hodMasterResponse || '' : '')
  const masterResponseDate =
    complaintAny.masterResponseDate ||
    (onboardReviewRole === 'MASTER' ? complaint.hodMasterResponseDate || '' : '')

  const hasHodOnboardData = hasAnyValue(hodResponse, hodResponseDate, complaint.hodSignature)
  const hasMasterOnboardData = hasAnyValue(masterResponse, masterResponseDate, complaint.masterSignature)
  const showOnboard = hasOnboardData || hasOnboardReviewMeta || hasHodOnboardData || hasMasterOnboardData
  const showOffice = complaint.onboardResolved !== true && (hasOfficeData || hasOfficeReviewMeta)
  const showAuthorities = hasAuthoritiesData || hasAuthoritiesReviewMeta
  const normalizedComplaintType =
    complaint.complaintType ||
    (complaint as any).complaintTypeName ||
    (complaint as any).complaint_type ||
    (complaint as any).complaint_type_name ||
    (complaint as any).typeOfComplaint ||
    (complaint as any).type_of_complaint

  return (
    <div style={WRAP_STYLE}>
      <div className='bg-white rounded shadow-lg' style={{ width: '78vw', maxWidth: 1050, maxHeight: '88vh', overflow: 'auto' }}>
          <div className='d-flex justify-content-between align-items-center border-bottom px-5 py-4'>
            <h5 className='modal-title'>Crew Complaint</h5>
            <button type='button' className='btn-close' onClick={onClose} />
          </div>
          <div className='p-5 text-dark'>
            <div className='d-flex align-items-center gap-3 mb-4'>
              <span className={`badge ${badge(complaint.status)}`}>{complaint.status}</span>
              {complaint.workflowStage && complaint.workflowStage !== 'CLOSED' && (
                <span className='badge badge-light-info'>{workflowStageLabel(complaint.workflowStage)}</span>
              )}
              {complaint.authoritiesReminderAt && (
                <span className='badge badge-danger'>Reminder Active</span>
              )}
              <span className='text-dark'>Seafarer: {complaint.seafarerName || complaint.crewName || '-'}</span>
              <span className='text-dark'>
                Vessel:{' '}
                <VesselLandingLink
                  vesselId={complaint.vesselId ?? null}
                  vesselName={complaint.vesselName || '-'}
                  className='fw-semibold text-dark text-hover-primary'
                />
              </span>
            </div>

            <div className='row g-3 mb-4'>
              <div className='col-md-3'><strong>Record Date:</strong> {complaint.recordDate || '-'}</div>
              <div className='col-md-3'><strong>Report Date:</strong> {complaint.reportDate || '-'}</div>
              <div className='col-md-3'><strong>Rank:</strong> {complaint.rankName || '-'}</div>
              <div className='col-md-3'><strong>Complaint Type:</strong> {complaintTypeLabel(normalizedComplaintType)}</div>
            </div>

            <div className='mb-4'>
              <h6>Description of Complaint</h6>
              <div className='border rounded p-3 bg-light'>{complaint.description || '-'}</div>
            </div>

            {showOnboard && (
              <div className='mb-4'>
                <h6>Onboard Response</h6>
                <div className='border rounded p-3'>
                  {hasHodOnboardData && (
                    <div className='mb-3'>
                      <div><strong>HOD Response:</strong> {hodResponse || '-'}</div>
                      <div><strong>HOD Response Date:</strong> {hodResponseDate || '-'}</div>
                      <div><strong>HOD Signature:</strong> {complaint.hodSignature || '-'}</div>
                    </div>
                  )}
                  {hasMasterOnboardData && (
                    <div className='mb-3'>
                      <div><strong>Master Response:</strong> {masterResponse || '-'}</div>
                      <div><strong>Master Response Date:</strong> {masterResponseDate || '-'}</div>
                      <div><strong>Master Signature:</strong> {complaint.masterSignature || '-'}</div>
                    </div>
                  )}
                  <div><strong>Prevent Recurrence:</strong> {complaint.onboardPreventRecurrence || '-'}</div>
                  <div><strong>Onboard Review As:</strong> {complaint.onboardReviewRole || '-'}</div>
                  <div><strong>Onboard Resolved:</strong> {complaint.onboardResolved === true ? 'Yes' : complaint.onboardResolved === false ? 'No' : '-'}</div>
                  <div><strong>Onboard Reviewed By:</strong> {complaint.onboardReviewedBy || '-'}</div>
                </div>
              </div>
            )}

            {showOffice && (
              <div className='mb-4'>
                <h6>Office Action</h6>
                <div className='border rounded p-3'>
                  <div><strong>Action Taken:</strong> {complaint.officeActionTaken || '-'}</div>
                  <div><strong>Action Date:</strong> {complaint.officeActionDate || '-'}</div>
                  <div><strong>Prevent Recurrence:</strong> {complaint.officePreventRecurrence || '-'}</div>
                  <div><strong>Office Resolved:</strong> {complaint.officeResolved === true ? 'Yes' : complaint.officeResolved === false ? 'No' : '-'}</div>
                  <div><strong>Welfare Officer Signature:</strong> {complaint.officeWelfareOfficerSignature || '-'}</div>
                  <div><strong>Resolution Date:</strong> {complaint.officeResolutionDate || '-'}</div>
                  <div><strong>Office Remarks:</strong> {complaint.officeRemarks || '-'}</div>
                </div>
              </div>
            )}

            {showAuthorities && (
              <div className='mb-2'>
                <h6>Authorities Action</h6>
                <div className='border rounded p-3'>
                  <div><strong>Action Taken:</strong> {complaint.authoritiesActionTaken || '-'}</div>
                  <div><strong>Action Date:</strong> {complaint.authoritiesActionDate || '-'}</div>
                  <div><strong>Prevent Recurrence:</strong> {complaint.authoritiesPreventRecurrence || '-'}</div>
                  <div><strong>Welfare Officer Signature:</strong> {complaint.authoritiesWelfareOfficerSignature || '-'}</div>
                  <div><strong>Resolution Date:</strong> {complaint.authoritiesResolutionDate || '-'}</div>
                </div>
              </div>
            )}

          </div>
          <div className='d-flex justify-content-end gap-3 border-top px-5 py-4'>
            <button type='button' className='btn btn-light' onClick={onClose}>Close</button>
          </div>
      </div>
    </div>
  )
}

export default ViewCrewComplaintModal
